/*#
# touch-orbit

`touchOrbit(camera)` gives an `ArcRotateCamera` the view controls people
expect from a 3D viewer, on every device. `<tosi-b3d>`'s default camera and
`orbitCam` both call it; call it yourself on an `ArcRotateCamera` you build.

| | orbit | pan | zoom |
| --- | --- | --- | --- |
| mouse | drag | **shift**-drag, right-drag, ctrl-drag | wheel |
| trackpad | click-drag | **two-finger drag** | pinch |
| touch | one finger | two fingers together | pinch |

- **Pan follows the pointer.** Pixels become world units at the TARGET's depth
  (`2·r·tan(fov/2)` per viewport height), applied straight to the target, so
  the point you grabbed moves with you at any distance. Babylon's own pan is a
  fixed `1/panningSensibility` fed through its inertia, so it slows relative to
  the view the further out you are.
- **A trackpad's two-finger drag pans; a pinch zooms.** Both arrive as `wheel`
  events, as a mouse wheel does: `wheelClassifier` (below) tells them apart (a pinch carries `ctrlKey`, a mouse notch
  is big, a trackpad moves sideways or starts small). No browser says which
  device sent a wheel event, so it is a heuristic; shift-drag pans on
  anything. `{ trackpadPan: false }` leaves the wheel to Babylon (it zooms).
- **Touch:** one finger orbits, two fingers moving together pan, a pinch
  zooms; the decision is made once per gesture (`twoFingerGesture`, below).
- **A wheel over a panel is the panel's**: panels take it before the canvas
  sees it, so a list scrolls and the scene stays put.

- **Zoom is proportional**: a wheel step or pinch moves the camera by a share
  of its distance (`WHEEL_SHARE`, as Babylon's `wheelDeltaPercentage`), so it
  is as gentle at arm's length as across a field. A page that set
  `wheelPrecision` or `wheelDeltaPercentage` itself keeps its own. A
  trackpad pinch scales the distance by the pinch (`PINCH_SHARE`): it sends
  many small steps, which the wheel's share per notch barely moved.
- **Zoom stops short of the target**: with no `lowerRadiusLimit` of your own,
  it is set to twice the camera's near plane (at least 1 cm), because Babylon
  has none and zooming in went through the target and flipped the view.

Returns a function that restores Babylon's handlers.

## Two fingers: pan OR zoom, decided once

The decision is pure (no Babylon, no DOM), so it is tested without a touchscreen.
Babylon's `ArcRotateCamera` does both on every two-finger move by default, with
zoom driven by the change in SQUARED finger distance. Real fingers never keep their
spacing exactly, so a two-finger drag reads mostly as zoom. Its other mode calls a
gesture a pinch only if the spacing jumps far in one event early on, so a slow,
deliberate pinch becomes a pan.

The rule here: compare how far the fingers have SPREAD (net change in spacing since
the gesture began) with how far their centroid has TRAVELLED (net, likewise), until
the two together pass `decidePx`. Then lock: whichever is larger wins for the rest
of the gesture. Net, not summed per event, because summed jitter reads as
spreading. A gesture never changes its mind mid-stroke, because that reads as the
camera fighting you.

Zoom is NATURAL: the radius scales by `previous spacing / spacing`, so what is under
your fingers stays under them. Pan is in pixels, converted to world units at the
target's depth as above.
*/
/*{ "parent": "input", "order": 40 }*/
import * as BABYLON from '@babylonjs/core'
import { twoFingerGesture, wheelClassifier } from './touch-gesture.js'

/** Move the camera's target so the scene follows the pointer by (dx, dy) px. */
function panBy(camera: BABYLON.ArcRotateCamera, dx: number, dy: number): void {
  const engine = camera.getEngine()
  const h = engine.getRenderHeight(true) || 1
  const perPx = (2 * camera.radius * Math.tan(camera.fov / 2)) / h
  const right = camera.getDirection(BABYLON.Axis.X)
  const up = camera.getDirection(BABYLON.Axis.Y)
  // Pointer right → the scene follows right → the target moves LEFT.
  right.scaleInPlace(-dx * perPx)
  up.scaleInPlace(dy * perPx)
  camera.target.addInPlace(right).addInPlace(up)
}

/** A wheel step's share of the camera's distance (Babylon's `wheelDeltaPercentage`). */
export const WHEEL_SHARE = 0.01

/**
 * A trackpad pinch's zoom per unit of `deltaY`: the distance scales by
 * `exp(deltaY * PINCH_SHARE)`, so doubling the fingers' spread (about 70
 * units in Chrome) roughly halves it.
 */
export const PINCH_SHARE = 0.01

export function touchOrbit(
  camera: BABYLON.ArcRotateCamera,
  options: { trackpadPan?: boolean } = {}
): () => void {
  // Once per camera: `<tosi-b3d>` applies it to any orbit camera it is given,
  // and a page may have called it already.
  const done = (camera as any)._tosiTouchOrbit as (() => void) | undefined
  if (done != null) return done
  // Zoom stops short of the target: Babylon's default has no floor, and the
  // radius went through zero and the view flipped. A limit a page set wins.
  if (camera.lowerRadiusLimit == null || camera.lowerRadiusLimit <= 0) {
    camera.lowerRadiusLimit = Math.max(0.01, camera.minZ * 2)
  }
  // The wheel zooms by a share of the distance, not a fixed one: Babylon's
  // default moved ~0.8 m a notch, so close up one notch went too close. A
  // page's own wheel setting wins.
  if (camera.wheelDeltaPercentage === 0 && camera.wheelPrecision === 3) {
    camera.wheelDeltaPercentage = WHEEL_SHARE
  }
  const pointers = camera.inputs?.attached?.pointers as
    | BABYLON.ArcRotateCameraPointersInput
    | undefined
  if (pointers == null) return () => {}
  const original = pointers.onMultiTouch
  const originalTouch = pointers.onTouch
  const gesture = twoFingerGesture()

  pointers.onMultiTouch = (_a, _b, prevSq, sq, prevPos, pos): void => {
    // Babylon's own bookends: the first call of a gesture has no previous,
    // the last has no current.
    if (prevSq === 0 && prevPos == null) {
      gesture.reset()
      return
    }
    if (sq === 0 && pos == null) {
      gesture.reset()
      return
    }
    if (prevPos == null || pos == null) return
    const step = gesture.step(Math.sqrt(prevSq), Math.sqrt(sq), prevPos, pos)
    if (step.kind === 'zoom') {
      const lo = camera.lowerRadiusLimit ?? 0.001
      const hi = camera.upperRadiusLimit ?? Infinity
      camera.radius = Math.min(hi, Math.max(lo, camera.radius * step.ratio))
    } else if (step.kind === 'pan') {
      panBy(camera, step.dx, step.dy)
    }
  }

  /*
  ONE POINTER: shift-drag pans, as do the presses Babylon's own input map
  calls a pan (right-drag, ctrl-drag), but by the follow-the-pointer rule.
  Babylon records the modifier keys on the input as each event arrives, and
  (since 9) which interaction the press resolved to as `_activeEntry`.
  */
  pointers.onTouch = (point, offsetX, offsetY): void => {
    const p = pointers as any
    const pan =
      p._activeEntry != null &&
      (p._shiftKey === true || p._activeEntry.interaction === 'pan')
    if (pan && camera.panningSensibility !== 0) panBy(camera, offsetX, offsetY)
    else originalTouch.call(pointers, point, offsetX, offsetY)
  }

  /*
  THE WHEEL, on the canvas in the capture phase: before Babylon's own wheel
  input (on the same canvas), after a panel's (on its parent), so a wheel
  over a panel never reaches here.
  */
  const canvas = camera.getEngine().getRenderingCanvas()
  const wheels = wheelClassifier()
  const onWheel = (e: WheelEvent) => {
    if (camera.getScene().activeCamera !== camera) return
    const kind = wheels.kind(e, e.timeStamp)
    if (e.ctrlKey && kind === 'zoom' && e.deltaMode === 0) {
      // A trackpad pinch: many small events, not a mouse's notches of ~100,
      // so the wheel's share per unit hardly moved the camera. Scale by the
      // pinch instead, as a touch pinch does.
      e.preventDefault()
      e.stopImmediatePropagation()
      const lo = camera.lowerRadiusLimit ?? 0.001
      const hi = camera.upperRadiusLimit ?? Infinity
      camera.radius = Math.min(
        hi,
        Math.max(lo, camera.radius * Math.exp(e.deltaY * PINCH_SHARE))
      )
      return
    }
    if (kind !== 'pan') return
    e.preventDefault()
    e.stopImmediatePropagation()
    // Natural scrolling: the fingers drag the scene.
    panBy(camera, -e.deltaX, -e.deltaY)
  }
  if (options.trackpadPan !== false)
    canvas?.addEventListener('wheel', onWheel, {
      capture: true,
      passive: false,
    })

  const restore = () => {
    pointers.onMultiTouch = original
    pointers.onTouch = originalTouch
    canvas?.removeEventListener('wheel', onWheel, { capture: true })
    delete (camera as any)._tosiTouchOrbit
  }
  ;(camera as any)._tosiTouchOrbit = restore
  return restore
}
